import { fetchJson, postJson, patchJson } from './client';
import type { TemplateAdoptionSummary } from '../types/templateAdoption';
import type { OutcomeMixSummary } from '../types/outcomeMix';
import type { ApprovalPerformanceSummary } from '../types/approvalPerformance';
import type { RejectionReasonsSummary } from '../types/rejectionReasons';
import type { WorkflowHealthSummary } from '../types/workflowHealth';
import type { ExceptionExpirySummary } from '../types/exceptionExpiry';
import type { FieldDefinition } from '../components/DynamicField';

// ─── Types ───

/**
 * These three mirror `PageDefinition`, `AuditEntry` and `WorkflowRun` on the server. They were
 * previously declared as `unknown[]`, which is not a neutral choice: a consumer cannot read
 * anything off `unknown` without asserting, so every page that needed these redeclared them
 * locally and the declarations drifted apart. Keeping the shape here means one contract.
 */
export interface RequestTypePage {
  key: string;
  label: string;
  description?: string;
  /** Keys of fields (from `fields`) rendered on this page. */
  fields: string[];
}

export interface RequestAuditEntry {
  timestamp: string;
  action: string;
  changedBy: string;
  changedById: string;
  changedByTenantId?: string | null;
  details?: string;
  fieldChanges?: Record<string, { oldValue?: string; newValue?: string }>;
}

export interface RequestWorkflowRun {
  runId?: string;
  triggeredAt: string;
  /** Always serialised by the server — `WorkflowRun.Trigger` defaults to a new instance. */
  trigger: { from: string; to: string };
  triggeredBy: string;
  status: string;
  actions: {
    key: string;
    status: string;
    startedAt?: string;
    completedAt?: string;
    output?: Record<string, string>;
    error?: string;
  }[];
  trackingDisposition?: string;
}

export interface AttachmentsConfig {
  provider?: 'blob' | 'ado';
  /** Master switch — must be explicitly true for the UI/API to expose attachments. */
  enabled?: boolean;
  /** When true, the request cannot be submitted unless at least one file is attached. */
  required?: boolean;
  /** When true (default) the server refuses upload/delete after submission to MSApprovals. */
  lockOnSubmit?: boolean;
}

/** One choice in a {@link Router}. `pathType` decides how the requestor is forwarded. */
export interface RouterOption {
  /** Stable id used in the `?option=` deep link (e.g. "corp", "restore"). */
  value: string;
  label: string;
  description?: string;
  /** `external` opens `externalUrl`; `template` forwards to `templateRequestType`'s submit form. */
  pathType: 'external' | 'template';
  externalUrl?: string;
  templateRequestType?: string;
}

/** Option selector that turns one tile into a router to distinct destinations. */
export interface Router {
  label?: string;
  prompt?: string;
  helperText?: string;
  options: RouterOption[];
}

export interface RequestTypeTemplate {
  requestType: string;
  displayName: string;
  description: string;
  icon: string;
  category?: string;
  /** Resolved server-side from template plus environment configuration. */
  catalogState?: string;
  /**
   * Not a self-service form — hidden from Request/Exception Central in every environment and not
   * renderable at /requests/new/{requestType}. Either feature plumbing created server-side, or a
   * retired type kept only so existing documents still render. Server enforces this too.
   */
  hideFromCatalog?: boolean;
  /** Guidance shown when a hidden type is opened as a form, or returned by POST /api/requests. */
  submitBlockedHint?: string;
  /** In-app route (or absolute URL) of the tool that supersedes this type, offered as a button. */
  submitBlockedHref?: string;
  /** Button label for `submitBlockedHref`. Lives on the template so no page hardcodes a feature name. */
  submitBlockedCta?: string;
  /**
   * Set by the server when this type's questions come from a published composition and none could
   * be served. The form has no fields for a reason nobody intended, so the page shows a temporary
   * notice rather than an empty wizard. The submit endpoint refuses with 503 regardless.
   */
  structureUnavailable?: boolean;
  /**
   * Published version of the admin-authored form this template was composed from; absent for a type
   * whose structure comes from its JSON file. Sent back on submit so the server can confirm the
   * structure did not change while the form was being filled in.
   */
  formVersion?: number;
  /**
   * Keys this form used to ask but no longer does — retired questions, and questions on a page the
   * admin disabled. A submitted request still carries those answers, so the detail page shows them
   * read-only. Enumerated by the server rather than inferred here: the backend also writes derived
   * bookkeeping into a request's data, and "anything that is not a field" would present all of it
   * as a question the user had been asked.
   */
  retiredFieldKeys?: string[];
  isException?: boolean;
  /** Display-only lookup form: renders fields/field actions but shows no Submit button. */
  lookupOnly?: boolean;
  /** Per-tenant configuration keyed by tenant GUID (e.g. FIC issuer policy id under `ficPolicyId`). */
  tenantConfig?: Record<string, Record<string, string>>;
  /**
   * When set, this tile is a router, not a form: the new-request page shows an option selector and
   * forwards to the chosen destination instead of rendering fields. Each option carries its own
   * `pathType`, so adding an option cannot inherit the wrong flow; resolution falls closed.
   */
  router?: Router;
  fields: FieldDefinition[];
  pages?: RequestTypePage[];
  // Mirrors StatusDefinition on the server, which always serialises `allowedFrom` (it defaults to
  // an empty list, meaning "initial status only"). Declaring it is not cosmetic: pages read
  // `s.allowedFrom.includes(...)` unguarded, so a declaration that omits it forces every consumer
  // to redeclare the type locally and drift.
  statuses: { value: string; label: string; color: string; allowedFrom: string[] }[];
  initialStatus: string;
  roles: { submit: string; manage: string };
  submitEndpoint?: string;
  requireValidation?: string[];
  resetFollowingPagesOnChange?: string[];
  attachments?: AttachmentsConfig;
  [key: string]: unknown;
}

export interface SubscriptionReclamationApprovalDisplay {
  serviceId: string;
  serviceName: string;
  tenantId: string;
  tenantName?: string | null;
  tenantDomain?: string | null;
  subscriptionNames: Record<string, string | null>;
  principalUpns: Record<string, string | null>;
}

export interface SubscriptionReclamationBatch {
  key: string;
  tenantId: string;
  serviceId: string;
  serviceName: string;
  subscriptionIds: string[];
  principalIds: string[];
  approvalDisplay?: SubscriptionReclamationApprovalDisplay | null;
  fulfillmentMethod: string;
  assignmentDurationDays?: number | null;
  status: string;
  message?: string | null;
  errorCode?: string | null;
  icmIncidentId?: string | null;
  icmIncidentUrl?: string | null;
  roleAssignmentIds: string[];
}

export interface SubscriptionReclamationPlan {
  serviceId?: string | null;
  approvalSubscriptionIds: string[];
  batches: SubscriptionReclamationBatch[];
  approvalOverride?: {
    recordedAt: string;
    actorTenantId: string;
    actorObjectId: string;
    reason: string;
    scopeHash: string;
    fromStatus?: string | null;
    targetStatus?: string | null;
  } | null;
}

export interface RequestDocument {
  id: string;
  requestType: string;
  title: string;
  status: string;
  /** Exception lifecycle flag kept in sync with status server-side: Active | Expired | Inactive. */
  exceptionStatus?: string;
  createdAt: string;
  createdById?: string;
  createdByTenantId?: string | null;
  isCurrentUserOwner?: boolean;
  requiresAdminForDetailEdits?: boolean;
  createdByName: string;
  createdByUserPrincipalName?: string;
  data: Record<string, unknown>;
  auditLog?: RequestAuditEntry[];
  workflowRuns?: RequestWorkflowRun[];
  diagnostic?: { severity?: string; message?: string; code?: string } | null;
  failureCode?: string | null;
  /** Set on a child request; carries the id of the container it was split out of. */
  parentRequestId?: string | null;
  /** Set only on a container request; how many children the split produced. */
  childRequestCount?: number | null;
  subscriptionReclamationPlan?: SubscriptionReclamationPlan | null;
  [key: string]: unknown;
}

// ─── Request Types ───

export async function getRequestTypes(): Promise<RequestTypeTemplate[]> {
  return fetchJson<RequestTypeTemplate[]>('/api/request-types');
}

export async function getManageableRequestTypes(): Promise<RequestTypeTemplate[]> {
  return fetchJson<RequestTypeTemplate[]>('/api/request-types/manageable');
}

export async function getRequestType(requestType: string): Promise<RequestTypeTemplate> {
  return fetchJson<RequestTypeTemplate>(`/api/request-types/${encodeURIComponent(requestType)}`);
}

/** One tile in the home page "most active requests" row (GET /api/request-types/popular). */
export interface PopularRequestType {
  requestType: string;
  displayName: string;
  description: string;
  icon: string;
  category: string;
  /** Org-wide submissions in the ranking window; a ranking signal only, not shown as a metric. */
  count: number;
}

/**
 * Most-submitted request types (org-wide, last N days) that the caller can actually launch. Feature-
 * gated on `Assistant.Guidance` server-side — only call it behind that flag, or it 404s.
 */
export async function getPopularRequestTypes(limit?: number, windowDays?: number): Promise<PopularRequestType[]> {
  const params = new URLSearchParams();
  if (limit !== undefined) params.set('limit', String(limit));
  if (windowDays !== undefined) params.set('windowDays', String(windowDays));
  const query = params.toString();
  return fetchJson<PopularRequestType[]>(`/api/request-types/popular${query ? `?${query}` : ''}`);
}

// B1/B2/B3 — template adoption. Org-wide, gated on AdoptionReportRead + Reports.TemplateAdoption
// (see Endpoints/RequestEndpoints.cs and docs/reporting-design.md §4 group B).
export async function getTemplateAdoptionReport(windowDays: number): Promise<TemplateAdoptionSummary> {
  return fetchJson<TemplateAdoptionSummary>(`/api/request-types/reports/adoption?windowDays=${encodeURIComponent(windowDays)}`);
}

// F1 — outcome mix by template. Org-wide, reusing AdoptionReportRead + Reports.OutcomeMix.
export async function getOutcomeMixReport(windowDays: number): Promise<OutcomeMixSummary> {
  return fetchJson<OutcomeMixSummary>(`/api/request-types/reports/outcome-mix?windowDays=${encodeURIComponent(windowDays)}`);
}

// F2 — approval performance. Same boundary and same policy as F1/F3: AdoptionReportRead +
// Reports.ApprovalPerformance.
export async function getApprovalPerformanceReport(windowDays: number): Promise<ApprovalPerformanceSummary> {
  return fetchJson<ApprovalPerformanceSummary>(`/api/request-types/reports/approval-performance?windowDays=${encodeURIComponent(windowDays)}`);
}

// F3 — rejection reasons by template. Same boundary and same policy as F1: AdoptionReportRead +
// Reports.RejectionReasons.
export async function getRejectionReasonsReport(windowDays: number): Promise<RejectionReasonsSummary> {
  return fetchJson<RejectionReasonsSummary>(`/api/request-types/reports/rejection-reasons?windowDays=${encodeURIComponent(windowDays)}`);
}

// F4 — workflow failure health by template. Same boundary and same policy as F1/F3:
// AdoptionReportRead + Reports.WorkflowHealth.
export async function getWorkflowHealthReport(windowDays: number): Promise<WorkflowHealthSummary> {
  return fetchJson<WorkflowHealthSummary>(`/api/request-types/reports/workflow-health?windowDays=${encodeURIComponent(windowDays)}`);
}

// F5 — exception expiry pipeline. Point-in-time snapshot, same Report Central boundary as F1/F3.
export async function getExceptionExpiryReport(): Promise<ExceptionExpirySummary> {
  return fetchJson<ExceptionExpirySummary>('/api/request-types/reports/exception-expiry');
}

// ─── Requests ───

export async function getMyRequests(): Promise<RequestDocument[]> {
  return fetchJson<RequestDocument[]>('/api/requests/mine');
}

export async function getRequest(id: string): Promise<RequestDocument> {
  return fetchJson<RequestDocument>(`/api/requests/${encodeURIComponent(id)}`);
}

/**
 * Returns the child requests a container was split into. Authorized against the parent, so a caller
 * who can open the parent gets the whole family or nothing — never a partial list that would
 * understate the size of the split.
 */
export async function getRequestChildren(id: string): Promise<RequestDocument[]> {
  return fetchJson<RequestDocument[]>(`/api/requests/${encodeURIComponent(id)}/children`);
}

export interface ChildCancellationResult {
  id: string;
  title: string;
  outcome: 'Cancelled' | 'Skipped' | 'RecallFailed' | 'Failed';
  message: string;
}

export async function cancelRemainingRequests(id: string): Promise<ChildCancellationResult[]> {
  return postJson<ChildCancellationResult[]>(`/api/requests/${encodeURIComponent(id)}/cancel-remaining`, {});
}

export async function createRequest(
  requestType: string,
  data: Record<string, string>,
  formVersion?: number,
): Promise<RequestDocument> {
  // formVersion is the structure the submitter actually filled in. The server refuses with 409 if an
  // admin published a new version in the meantime, so the artifact's version stamp is always the one
  // these answers were given against.
  return postJson<RequestDocument>('/api/requests', { requestType, data, formVersion });
}

export interface GetRequestsByTypeOptions {
  createdSince?: string;
}

export async function getRequestsByType(
  requestType: string,
  options: GetRequestsByTypeOptions = {},
): Promise<RequestDocument[]> {
  const query = new URLSearchParams();
  if (options.createdSince) query.set('createdSince', options.createdSince);
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return fetchJson<RequestDocument[]>(`/api/requests/by-type/${encodeURIComponent(requestType)}${suffix}`);
}

export async function updateRequestStatus(id: string, status: string, approvalOverrideReason?: string): Promise<RequestDocument> {
  return patchJson<RequestDocument>(`/api/requests/${encodeURIComponent(id)}/status`, {
    status, ...(approvalOverrideReason !== undefined ? { approvalOverrideReason } : {}),
  });
}

/**
 * Revokes an exception by marking its ExceptionStatus as "Expired" (the backend reverse-syncs the
 * request status to "Expired"), removing it from the active/granted list. Note: this is a
 * lifecycle-only revoke — it does not yet de-provision the underlying Entra grant.
 */
export async function revokeException(id: string): Promise<RequestDocument> {
  return patchJson<RequestDocument>(`/api/requests/${encodeURIComponent(id)}/status`, { exceptionStatus: 'Expired' });
}

export async function updateRequestData(id: string, data: Record<string, string>): Promise<RequestDocument> {
  return patchJson<RequestDocument>(`/api/requests/${encodeURIComponent(id)}/data`, { data });
}

export async function adjustExceptionExpiry(id: string, expiryDate: string): Promise<RequestDocument> {
  return postJson<RequestDocument>(`/api/requests/${encodeURIComponent(id)}/exception-expiry`, { expiryDate });
}

// ─── Bulk exception upload ───

/** One CSV-derived row submitted to the bulk exception endpoint. */
export interface BulkExceptionRow {
  /** Application (client) ID the exception applies to. */
  appId: string;
  /** Trusted issuer authority ID. Required for the legacy SNI exception type. */
  authorityId?: string;
  /** Trusted issuer subject name. Required for the legacy SNI exception type. */
  subjectName?: string;
  /** Exception expiry date (ISO / YYYY-MM-DD). */
  expiryDate: string;
  /** Exception owner (UPN/email) recorded as the requestor for expiry notifications. */
  owner: string;
  /** Business rationale stored on the exception request and shown to approvers. */
  businessJustification: string;
}

/** Per-row outcome returned by the bulk exception endpoint. */
export interface BulkExceptionRowResult {
  row: number;
  appId: string;
  status: 'created' | 'skipped' | 'error';
  message: string;
  id?: string;
}

export interface BulkExceptionResult {
  requestType: string;
  total: number;
  created: number;
  skipped: number;
  errors: number;
  results: BulkExceptionRowResult[];
}

/**
 * Bulk-creates pre-approved exceptions for the given exception type. The backend validates each row,
 * skips apps already covered by an active/in-flight exception, and records the row's owner as the
 * requestor so expiry notifications reach the owner rather than the admin performing the upload.
 * For policy-verified types (e.g. FIC issuer) a target `tenantId` is required and each app is checked
 * against the live app management policy before a tracking record is backfilled.
 */
export async function bulkUploadExceptions(
  requestType: string,
  rows: BulkExceptionRow[],
  tenantId?: string,
  mode: 'existing' | 'new' = 'existing',
): Promise<BulkExceptionResult> {
  return postJson<BulkExceptionResult>('/api/requests/bulk-exceptions', { requestType, rows, tenantId, mode });
}
